'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { BaseModal } from './BaseModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Spinner } from './loading/Spinner';
import {
  useIssueServiceTokenMutation,
  useRevokeServiceTokenMutation,
  useServiceTokens,
} from '../hooks/queries/useServiceTokens';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { getAuthSession } from '../lib/authSession';
import type { IssuedServiceToken, ServiceToken, ServiceTokenScope } from '../lib/api';
import { copyTextToClipboard } from '../lib/clipboard';

type ServiceTokenSettingsViewProps = {
  projectId: string;
};

const DEFAULT_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
/** Mirrors SERVICE_TOKEN_MAX_TTL_MS in api/src/services/serviceToken.ts; the server rejects anything longer. */
const MAX_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * `<input type="datetime-local">` reads and writes **local** wall-clock time, and
 * `new Date(value)` on submit parses it back as local. Feeding it `toISOString()`
 * put the value, the `min` floor and the parse on two different clocks, so every
 * expiry was off by the UTC offset — 30 days minus 9 hours in KST, and a `min`
 * that either allowed past times or blocked the next several hours.
 */
export const toLocalDateTimeInput = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

const defaultExpiryInput = () => toLocalDateTimeInput(new Date(Date.now() + DEFAULT_EXPIRY_MS));

const getStatus = (serviceToken: ServiceToken): 'active' | 'expired' | 'revoked' => {
  if (serviceToken.revokedAt) return 'revoked';
  if (new Date(serviceToken.expiresAt) <= new Date()) return 'expired';
  return 'active';
};

/**
 * 프로젝트 설정의 CI 서비스 토큰 탭.
 *
 * 토큰은 개인 자격증명이 아니라 **프로젝트 소유** 자격증명이다(ServiceToken.projectId, 권한도
 * `serviceTokens.read`/`.write`). 그래서 계정 범위 화면이나 에이전트 목록이 아니라 프로젝트 설정에 둔다.
 */
export default function ServiceTokenSettingsView({ projectId }: ServiceTokenSettingsViewProps) {
  const t = useTranslations('workspace.page.serviceTokens');
  const tCommon = useTranslations('common');
  const session = useMemo(() => getAuthSession(), []);
  const token = session?.token ?? null;
  const { can } = useProjectPermissions({ projectId });
  const canWrite = can('serviceTokens.write');
  const { data: serviceTokens = [], isLoading } = useServiceTokens(token, projectId);
  const issueMutation = useIssueServiceTokenMutation(token, projectId);
  const revokeMutation = useRevokeServiceTokenMutation(token, projectId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ServiceTokenScope>('read');
  const [expiresAt, setExpiresAt] = useState(defaultExpiryInput);
  const [issued, setIssued] = useState<IssuedServiceToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ServiceToken | null>(null);

  const activeTokens = useMemo(
    () => serviceTokens.filter((serviceToken) => getStatus(serviceToken) === 'active'),
    [serviceTokens],
  );

  const resetCreateForm = () => {
    setName('');
    setScope('read');
    setExpiresAt(defaultExpiryInput());
  };

  const handleIssue = async () => {
    if (!name.trim() || !expiresAt) return;
    try {
      const result = await issueMutation.mutateAsync({
        name: name.trim(),
        scope,
        expiresAt: new Date(expiresAt).toISOString(),
      });
      setIsCreateOpen(false);
      resetCreateForm();
      setIssued(result);
    } catch {
      toast.error(t('issueFailed'));
    }
  };

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await copyTextToClipboard(issued.token);
      toast.success(t('copied'));
    } catch {
      toast.error(t('copyFailed'));
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
      toast.success(t('revoked'));
    } catch {
      toast.error(t('revokeFailed'));
    }
  };

  return (
    <>
      <section>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">{t('title')}</h2>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[var(--color-fg-muted)]">{t('description')}</p>
          </div>
          {canWrite ? (
            <button type="button" className="btn btn-primary btn-sm shrink-0" onClick={() => setIsCreateOpen(true)}>
              {t('create')}
            </button>
          ) : null}
        </div>

        {isLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-[var(--color-fg-muted)]">
            <Spinner />
            {tCommon('loading')}
          </div>
        ) : serviceTokens.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--color-fg-muted)]">{t('empty')}</p>
        ) : (
          <div className="mt-4 space-y-2">
            {serviceTokens.map((serviceToken) => {
              const status = getStatus(serviceToken);
              return (
                <div
                  key={serviceToken.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-base-200 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{serviceToken.name}</span>
                      <span className="badge badge-ghost badge-sm">{t(`scope.${serviceToken.scope}`)}</span>
                      <span className="badge badge-outline badge-sm">{t(`status.${status}`)}</span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--color-fg-subtle)]">
                      {t('expiresAt', { value: new Date(serviceToken.expiresAt).toLocaleString() })}
                    </p>
                  </div>
                  {status === 'active' && canWrite ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm text-error"
                      onClick={() => setRevokeTarget(serviceToken)}
                    >
                      {t('revoke')}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {activeTokens.length > 0 ? (
          <p className="mt-3 text-xs text-[var(--color-fg-subtle)]">
            {t('activeCount', { count: activeTokens.length })}
          </p>
        ) : null}
      </section>

      <BaseModal
        isOpen={isCreateOpen}
        onClose={() => {
          if (!issueMutation.isPending) setIsCreateOpen(false);
        }}
        canClose={!issueMutation.isPending}
        variant="bottomSheet"
        size="md"
      >
        <div>
          <h2 className="text-lg font-bold">{t('createTitle')}</h2>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('createDescription')}</p>
          <div className="mt-5 space-y-4">
            <label className="form-control">
              <span className="label-text mb-1">{t('name')}</span>
              <input
                className="input input-bordered w-full"
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="form-control">
              <span className="label-text mb-1">{t('scopeLabel')}</span>
              <select
                className="select select-bordered w-full"
                value={scope}
                onChange={(event) => setScope(event.target.value as ServiceTokenScope)}
              >
                <option value="read">{t('scope.read')}</option>
                <option value="write">{t('scope.write')}</option>
              </select>
            </label>
            <label className="form-control">
              <span className="label-text mb-1">{t('expiryLabel')}</span>
              <input
                className="input input-bordered w-full"
                type="datetime-local"
                value={expiresAt}
                min={toLocalDateTimeInput(new Date())}
                max={toLocalDateTimeInput(new Date(Date.now() + MAX_EXPIRY_MS))}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={issueMutation.isPending}
              onClick={() => setIsCreateOpen(false)}
            >
              {tCommon('actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!name.trim() || !expiresAt || issueMutation.isPending}
              onClick={() => void handleIssue()}
            >
              {issueMutation.isPending ? <Spinner /> : null}
              {t('create')}
            </button>
          </div>
        </div>
      </BaseModal>

      <BaseModal isOpen={issued !== null} onClose={() => setIssued(null)} variant="bottomSheet" size="md">
        <div>
          <h2 className="text-lg font-bold">{t('issuedTitle')}</h2>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('issuedDescription')}</p>
          <code className="mt-4 block break-all rounded-xl bg-base-300 p-4 font-mono text-sm">{issued?.token}</code>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => setIssued(null)}>
              {tCommon('actions.close')}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleCopy()}>
              {tCommon('actions.copy')}
            </button>
          </div>
        </div>
      </BaseModal>

      <DeleteConfirmModal
        isOpen={revokeTarget !== null}
        title={t('revokeTitle')}
        message={t('revokeMessage', { name: revokeTarget?.name ?? '' })}
        confirmLabel={t('revoke')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={revokeMutation.isPending}
        onConfirm={() => void handleRevoke()}
        onCancel={() => {
          if (!revokeMutation.isPending) setRevokeTarget(null);
        }}
      />
    </>
  );
}
